import { formatAddressName } from './format-address-name'
import { formatPosition } from './format-position'

/**
 * @typedef {Object} LocationOrPositionOrAddressId
 * @property {LocationDescription}  [locationDescription]   - Already translated position & params
 * @property {Id}                   [addressId]             - The current position address id
 */

/**
 * Helper function for formatting a position based on address information or location description
 *
 * @param {Object} intl - The modux intl
 * @param {LocationOrPositionOrAddressId} locationOrPositionOrAddressId - The object containing location, position, or address information
 * @param {Object.<string, Address>} [addresses] - All available addresses
 * @param {Object} [options] - Additional formatting options
 * @param {boolean} [options.asString=true] - Flag indicating whether the result should be formatted as a string (default: true)
 * @param {string[]} [options.locationOrPositionOrAddressIdFieldNames=['addressId', 'locationDescription']] - Array of field names to check for privacy fields (default: ['addressId', 'locationDescription'])
 * @returns {string} The formatted address or position
 */
export function formatAddressOrPosition(intl, locationOrPositionOrAddressId, addresses, options) {
    const { asString = true, locationOrPositionOrAddressIdFieldNames = ['addressId', 'locationDescription'] } =
        typeof options === 'object' ? options : {}
    const { addressId, locationDescription, privacyFields } = locationOrPositionOrAddressId
    const privacyCheck = element => locationOrPositionOrAddressIdFieldNames.includes(element)
    const privateLocation = privacyFields !== undefined && privacyFields.length > 0 && privacyFields.some(privacyCheck)

    if (privateLocation) {
        return intl.translate('wf_private_location')
    }

    const formattedPosition = formatPosition(
        intl,
        {
            locationDescription
        },
        { asString }
    )

    if (addressId && addresses) {
        const address = addresses[addressId]

        if (address) {
            /**
             * The 'address.name' is coming from the address selector, if
             * no selector is used format the name manually via 'formatAddressName'.
             */
            const addressName = address.name || formatAddressName(address)

            return `${addressName}, ${formattedPosition}`
        }
    }

    return formattedPosition
}
